import { z } from 'zod';
import { LocalDate } from './common.js';
/**
 * Sondaggi (docs/03-modello-dati.md §2.7): li crea la redazione dal pannello (solo admin) e sul
 * sito si vedono solo quelli attivi, fino alla data di chiusura. Si risponde senza account: alla
 * fine si conferma l'email con un codice, così ogni persona risponde una volta.
 */
export const SURVEY_STATUSES = ['draft', 'active', 'closed'];
/** una scelta, più scelte, testo libero, voto da 1 a 5 */
export const QUESTION_TYPES = ['single', 'multiple', 'text', 'rating'];
export const MAX_QUESTIONS = 15;
export const MAX_OPTIONS = 10;
export const SurveyQuestion = z
    .object({
    /** stabile: le risposte lo usano come chiave */
    id: z.string().regex(/^q[0-9a-z]{1,12}$/),
    type: z.enum(QUESTION_TYPES),
    label: z.string().trim().min(3).max(300),
    /** testo di aiuto sotto la domanda */
    hint: z.string().trim().max(300).optional(),
    options: z.array(z.string().trim().min(1).max(120)).max(MAX_OPTIONS).optional(),
    required: z.boolean().default(true),
})
    .refine((q) => q.type === 'single' || q.type === 'multiple'
    ? (q.options?.length ?? 0) >= 2 && new Set(q.options).size === q.options.length
    : !q.options?.length, { message: 'le domande a scelta hanno da 2 a 10 risposte diverse, le altre nessuna' });
/** Quello che scrive la redazione. */
export const SurveyInput = z
    .object({
    title: z.string().trim().min(5).max(120),
    /** perché lo chiediamo: in cima al modulo */
    intro: z.string().trim().max(600).default(''),
    questions: z.array(SurveyQuestion).min(1).max(MAX_QUESTIONS),
    /** ultimo giorno in cui si risponde (compreso); senza data resta aperto finché non si chiude */
    endsOn: LocalDate.optional(),
})
    .refine((s) => new Set(s.questions.map((q) => q.id)).size === s.questions.length, {
    message: 'due domande con lo stesso id',
});
/** Controlla le risposte rispetto alle domande; restituisce le risposte pulite o l'errore. */
export function checkAnswers(questions, raw) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw))
        return { ok: false, error: 'risposte non valide' };
    const input = raw;
    const out = {};
    for (const q of questions) {
        const v = input[q.id];
        const empty = v === undefined ||
            v === null ||
            (typeof v === 'string' && !v.trim()) ||
            (Array.isArray(v) && v.length === 0);
        if (empty) {
            if (q.required)
                return { ok: false, error: 'risposta mancante', question: q.id };
            continue;
        }
        const opts = q.options ?? [];
        if (q.type === 'single') {
            if (typeof v !== 'string' || !opts.includes(v))
                return { ok: false, error: 'scelta non valida', question: q.id };
            out[q.id] = v;
        }
        else if (q.type === 'multiple') {
            if (!Array.isArray(v) || v.some((x) => typeof x !== 'string' || !opts.includes(x)))
                return { ok: false, error: 'scelta non valida', question: q.id };
            out[q.id] = opts.filter((o) => v.includes(o));
        }
        else if (q.type === 'rating') {
            if (typeof v !== 'number' || !Number.isInteger(v) || v < 1 || v > 5)
                return { ok: false, error: 'voto non valido', question: q.id };
            out[q.id] = v;
        }
        else {
            if (typeof v !== 'string')
                return { ok: false, error: 'testo non valido', question: q.id };
            out[q.id] = v.trim().slice(0, 2000);
        }
    }
    return { ok: true, answers: out };
}
/** Aperto oggi: attivo e non oltre la data di chiusura. */
export const surveyOpen = (s, today) => s.status === 'active' && (!s.endsOn || today <= s.endsOn);
//# sourceMappingURL=survey.js.map